import { noteInternalError, state, threshold } from './config'
import { markSubmitStarted, markSubmitSucceeded } from './forms'
import { notifyEffect } from './interactions'
import { isActive, journeyId } from './journey'
import { normalizeKeys, scrubPath } from './scrub'
import { record, reserve, snapshot } from './transport'
import Http from '@/Utils/Http'
import { generateUUID } from '@/Utils/uuid'
import { http } from '@inertiajs/vue3'
import axios from 'axios'

const PENDING_TTL_MS = 120000
const TRIGGER_WINDOW_MS = 1000
const MAX_ERROR_BODY = 65536

const axiosRequests = new WeakMap()
const visitIds = new Map()

let pendingStarts = []
let pendingHttp = []

export function recentTrigger(now = Date.now()) {
  const trigger = state.lastTrigger

  return trigger && now - trigger.at <= TRIGGER_WINDOW_MS ? trigger : null
}

function sameOriginUrl(value, base) {
  try {
    const url = new URL(value, base ? new URL(base, window.location.href) : window.location.href)

    return url.origin === window.location.origin ? url : null
  } catch {
    return null
  }
}

function hrefWithoutHash(url) {
  const copy = new URL(url.href)

  copy.hash = ''

  return copy.href
}

// Visits are bound to their XHR in http.onRequest, which runs after `start` (FIFO by method and URL).
export function registerVisitStart(entry) {
  const now = Date.now()

  pendingStarts = pendingStarts.filter((start) => now - start.start < PENDING_TTL_MS)
  pendingStarts.push(entry)
}

export function forgetVisit(entry) {
  pendingStarts = pendingStarts.filter((start) => start !== entry)

  visitIds.delete(entry.correlationId)
}

function emitXhr(request, outcome, { status, errorKeys } = {}) {
  record(
    'xhr.request',
    {
      target: `${request.method.toUpperCase()} ${request.path ?? '/'}`,
      status,
      duration_ms: Date.now() - request.start,
      correlation_id: request.id,
      props: {
        client: request.client,
        outcome,
        error_keys: normalizeKeys(errorKeys, 20),
        trigger_seq: request.trigger?.seq,
      },
    },
    reserve(request.snap)
  )
}

function trackRequest(client, method, url, id) {
  const trigger = recentTrigger()

  state.stats.xhr += 1
  notifyEffect('request')

  if (method !== 'get') {
    markSubmitStarted(trigger)
  }

  return {
    id,
    client,
    method,
    href: url.href,
    path: scrubPath(url.pathname + url.search),
    start: Date.now(),
    trigger,
    snap: snapshot(),
  }
}

function settleSuccess(request, status) {
  if (request.method !== 'get' && status >= 200 && status < 300) {
    markSubmitSucceeded(request.trigger)
  }

  if (Date.now() - request.start >= threshold('slow_xhr_ms')) {
    emitXhr(request, 'slow', { status })
  }
}

function errorKeysFrom(status, data) {
  if (status !== 422) {
    return []
  }

  let body = data

  if (typeof body === 'string') {
    if (body.length > MAX_ERROR_BODY) {
      return []
    }

    try {
      body = JSON.parse(body)
    } catch {
      return []
    }
  }

  return body && typeof body.errors === 'object' && body.errors !== null ? Object.keys(body.errors) : []
}

function onInertiaRequest(config) {
  if (!isActive()) {
    return config
  }

  const url = sameOriginUrl(config.url)

  if (!url) {
    return config
  }

  const id = generateUUID()
  const method = String(config.method || 'get').toLowerCase()
  const headers = { ...(config.headers ?? {}), 'X-Request-Id': id, 'X-Journey-Id': journeyId() }
  const now = Date.now()

  pendingHttp = pendingHttp.filter((request) => now - request.start < PENDING_TTL_MS)
  visitIds.forEach((startedAt, visitId) => {
    if (now - startedAt >= PENDING_TTL_MS) {
      visitIds.delete(visitId)
    }
  })

  if (headers['X-Inertia']) {
    const href = hrefWithoutHash(url)
    const entry = pendingStarts.find((start) => !start.correlationId && start.method === method && start.href === href)

    if (entry) {
      entry.correlationId = id
      pendingStarts = pendingStarts.filter((start) => start !== entry)
    }

    visitIds.set(id, Date.now())
  } else {
    pendingHttp.push(trackRequest('http', method, url, id))
  }

  // A new headers object: visit.headers stays untouched so prefetch cache matching keeps working.
  return { ...config, headers }
}

function takeHttpRequest(headers, url) {
  const echoed = headers?.['x-request-id']
  let request = echoed ? pendingHttp.find((pending) => pending.id === echoed) : null

  if (!request && url) {
    const href = sameOriginUrl(url)?.href
    request = href ? pendingHttp.find((pending) => pending.href === href) : null
  }

  // Without an echoed id a response can only be matched when no visit is in flight.
  if (!request && !echoed && !url && visitIds.size === 0) {
    request = pendingHttp[0] ?? null
  }

  if (request) {
    pendingHttp = pendingHttp.filter((pending) => pending !== request)
  }

  return request
}

function onInertiaResponse(response) {
  if (isActive() && !response?.headers?.['x-inertia'] && !visitIds.has(response?.headers?.['x-request-id'])) {
    const request = takeHttpRequest(response.headers, null)

    if (request) {
      settleSuccess(request, response.status)
    }
  }

  return response
}

function onInertiaError(error) {
  const headers = error?.response?.headers

  if (!isActive() || headers?.['x-inertia'] || visitIds.has(headers?.['x-request-id'])) {
    return
  }

  const request = takeHttpRequest(headers, error?.url)

  if (!request) {
    return
  }

  if (error.code === 'ERR_CANCELLED') {
    emitXhr(request, 'aborted')
  } else if (error.code === 'ERR_NETWORK' || !error.response) {
    emitXhr(request, 'network_error')
  } else {
    const status = error.response.status

    emitXhr(request, 'failed', { status, errorKeys: errorKeysFrom(status, error.response.data) })
  }
}

function setHeader(config, name, value) {
  if (typeof config.headers?.set === 'function') {
    config.headers.set(name, value)
  } else {
    config.headers = { ...(config.headers ?? {}), [name]: value }
  }
}

function onAxiosRequest(config) {
  if (!isActive()) {
    return config
  }

  const url = sameOriginUrl(config.url ?? '', config.baseURL)

  if (!url) {
    return config
  }

  const id = generateUUID()

  setHeader(config, 'X-Request-Id', id)
  setHeader(config, 'X-Journey-Id', journeyId())
  axiosRequests.set(config, trackRequest('axios', String(config.method || 'get').toLowerCase(), url, id))

  return config
}

function onAxiosResponse(response) {
  const request = response?.config ? axiosRequests.get(response.config) : null

  if (request && isActive()) {
    axiosRequests.delete(response.config)
    settleSuccess(request, response.status)
  }
}

function onAxiosError(error) {
  const request = error?.config ? axiosRequests.get(error.config) : null

  if (!request || !isActive()) {
    return
  }

  axiosRequests.delete(error.config)

  if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
    emitXhr(request, 'aborted')
  } else if (!error.response) {
    emitXhr(request, 'network_error')
  } else {
    const status = error.response.status

    emitXhr(request, 'failed', { status, errorKeys: errorKeysFrom(status, error.response.data) })
  }
}

// Interceptors must hand back what they were given even if recording throws.
function passConfig(handler) {
  return (config) => {
    try {
      return handler(config) ?? config
    } catch (error) {
      noteInternalError(error)

      return config
    }
  }
}

function passResponse(handler) {
  return (response) => {
    try {
      handler(response)
    } catch (error) {
      noteInternalError(error)
    }

    return response
  }
}

function passError(handler) {
  return (error) => {
    try {
      handler(error)
    } catch (caught) {
      noteInternalError(caught)
    }

    return Promise.reject(error)
  }
}

export function installCorrelation() {
  http.onRequest(passConfig(onInertiaRequest))
  http.onResponse(passResponse(onInertiaResponse))
  http.onError((error) => {
    try {
      onInertiaError(error)
    } catch (caught) {
      noteInternalError(caught)
    }
  })

  new Set([axios, Http]).forEach((instance) => {
    instance.interceptors.request.use(passConfig(onAxiosRequest))
    instance.interceptors.response.use(passResponse(onAxiosResponse), passError(onAxiosError))
  })
}
